import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { buildDataTableLabels } from "@/components/table";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  CircleDot,
  Eye,
  Handshake,
  MessageCircle,
  RefreshCw,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { MessengerChat } from "@/components/chat/MessengerChat";
import type { ChatMessage } from "@/components/chat/types";
import { BookingStatusBadge } from "@/components/bookings";
import { ErrorState, ListResultsCard, LoadingState } from "@/components/common";
import { AdminFilterMenu } from "@/components/admin/AdminFilterMenu";
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmModal,
  EmptyState,
  FilterBar,
  Input,
  MasterDetailShell,
  masterDetailListItemClass,
  Modal,
  ModalFooter,
  Pagination,
  SearchField,
  Select,
  Switch,
  TruncatedText,
} from "@/components/ui";
import {
  formatActivityLocalVisitSummary,
  scheduleTimeToHHmm,
  todayActivityLocalDateInput,
  visitAtIsoToDateInput,
  visitAtIsoToTimeInput,
  visitAtLocalPickerToApiIso,
} from "@/components/handshake/visitAt";
import { toastApiError } from "@/lib/errorMessages";
import { formatDateTime } from "@/lib/datetime";
import { getIntlLocale } from "@/i18n";
import type { RegionCode } from "@/types";
import { cn } from "@/lib/utils";
import { bookingService, type RefundChoice } from "@/services/bookingService";
import {
  customerSupportChatKeys,
  customerSupportChatService,
  type CustomerSupportChatListItem,
  type CustomerSupportChatStatus,
} from "@/services/customerSupportChatService";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAuthStore } from "@/store/authStore";
import { Permission } from "@/types";

const PAGE_SIZE = 20;

type StatusFilter = "open" | "closed" | "all";

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  return formatDateTime(iso);
}

function formatBookingVisitWhen(
  visitAt: string,
  timeSlot: string | null | undefined,
  regionCode: RegionCode,
): string {
  return (
    formatActivityLocalVisitSummary(visitAt, {
      regionCode,
      timeSlot,
      locale: getIntlLocale(),
    }) ?? (timeSlot?.trim() ? scheduleTimeToHHmm(timeSlot) : "")
  );
}

function chatMessagesToUi(
  messages: { id: string; content: string; created_at: string; is_from_admin: boolean }[],
): ChatMessage[] {
  return messages.map((message) => ({
    id: message.id,
    content: message.content,
    createdAt: message.created_at,
    role: message.is_from_admin ? "self" : "other",
    senderLabel: message.is_from_admin ? undefined : "Customer",
    showReadReceipt: message.is_from_admin,
  }));
}

function ChatListItem({
  chat,
  selected,
  onSelect,
}: {
  chat: CustomerSupportChatListItem;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation("support");
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(masterDetailListItemClass(selected), "border-b border-surface-200/70")}
    >
      <div className="flex items-center justify-between gap-2">
        <TruncatedText className="font-medium text-surface-900">
          {chat.customer_contact}
        </TruncatedText>
        <div className="flex shrink-0 items-center gap-1">
          {chat.status === "CLOSED" ? (
            <Badge variant="default" size="sm">
              {t("customerSupportChats.closedBadge")}
            </Badge>
          ) : null}
          {chat.is_guest ? (
            <Badge variant="warning" size="sm">
              {t("customerSupportChats.guest")}
            </Badge>
          ) : null}
        </div>
      </div>
      <TruncatedText as="p" className="text-xs text-surface-500">
        {chat.latest_message_preview ?? t("customerSupportChats.noMessages")}
      </TruncatedText>
      <p className="text-[11px] text-surface-400">
        {formatWhen(chat.latest_message_at ?? chat.updated_at)}
      </p>
    </button>
  );
}

export function CustomerSupportChatsPanel() {
  const { t } = useTranslation("support");
  const { t: tCommon } = useTranslation("common");
  const tableLabels = buildDataTableLabels(tCommon);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canAmendBookings = hasPermission(Permission.BOOKINGS_AMEND_ANY);
  const canCancelAnyBooking = hasPermission(Permission.BOOKINGS_CANCEL_ANY);
  const canForceRefund = hasPermission(Permission.BOOKINGS_REFUND_ANY);
  const canReopenHandshake = hasPermission(Permission.HANDSHAKES_SESSION_REOPEN);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => searchParams.get("chatId"),
  );
  const [isRefreshingChat, setIsRefreshingChat] = useState(false);
  const [amendBookingId, setAmendBookingId] = useState<string | null>(null);
  const [amendActivityRegionCode, setAmendActivityRegionCode] = useState<
    RegionCode | null
  >(null);
  const [visitDate, setVisitDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("10:00");
  const [overrideCapacity, setOverrideCapacity] = useState(false);
  const [cancelBookingId, setCancelBookingId] = useState<string | null>(null);
  const [refundChoice, setRefundChoice] = useState<RefundChoice>("DEFAULT");
  const [forceCancelConfirmOpen, setForceCancelConfirmOpen] = useState(false);
  const [reopenTarget, setReopenTarget] = useState<{
    chatId: string;
    sessionId: string;
  } | null>(null);

  const chatIdFromUrl = searchParams.get("chatId");

  useEffect(() => {
    if (chatIdFromUrl) {
      setSelectedId(chatIdFromUrl);
    }
  }, [chatIdFromUrl]);

  const selectChat = (chatId: string) => {
    setSelectedId(chatId);
    const next = new URLSearchParams(searchParams);
    next.set("chatId", chatId);
    setSearchParams(next, { replace: true });
  };

  const listParams = useMemo(() => {
    const base = {
      search: debouncedSearch || undefined,
      skip: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    };
    if (statusFilter === "open") {
      return { ...base, open_only: true };
    }
    if (statusFilter === "closed") {
      return { ...base, status: "CLOSED" as CustomerSupportChatStatus };
    }
    return base;
  }, [debouncedSearch, page, statusFilter]);

  const listQuery = useQuery({
    queryKey: customerSupportChatKeys.list(listParams),
    queryFn: () => customerSupportChatService.list(listParams),
    refetchInterval: 15_000,
  });

  const totalPages = Math.max(
    1,
    Math.ceil((listQuery.data?.total ?? 0) / PAGE_SIZE),
  );

  const detailQuery = useQuery({
    queryKey: customerSupportChatKeys.detail(selectedId ?? ""),
    queryFn: () => customerSupportChatService.getById(selectedId!),
    enabled: selectedId != null,
    refetchInterval: selectedId ? 10_000 : false,
  });

  const contextQuery = useQuery({
    queryKey: customerSupportChatKeys.context(selectedId ?? ""),
    queryFn: () => customerSupportChatService.getContext(selectedId!),
    enabled: selectedId != null,
    staleTime: 0,
    refetchInterval: selectedId ? 10_000 : false,
  });

  const sendMutation = useMutation({
    mutationFn: ({ chatId, content }: { chatId: string; content: string }) =>
      customerSupportChatService.sendMessage(chatId, content),
    onSuccess: () => {
      if (selectedId) {
        void queryClient.invalidateQueries({
          queryKey: customerSupportChatKeys.detail(selectedId),
        });
        void queryClient.invalidateQueries({
          queryKey: customerSupportChatKeys.lists(),
        });
      }
    },
    onError: (err) => toastApiError(err),
  });

  const closeMutation = useMutation({
    mutationFn: (chatId: string) => customerSupportChatService.close(chatId),
    onSuccess: () => {
      toast.success(t("customerSupportChats.closed"));
      void queryClient.invalidateQueries({
        queryKey: customerSupportChatKeys.all,
      });
    },
    onError: (err) => toastApiError(err),
  });

  const reopenMutation = useMutation({
    mutationFn: ({
      chatId,
      sessionId,
    }: {
      chatId: string;
      sessionId: string;
    }) => customerSupportChatService.reopenHandshake(chatId, sessionId),
    onSuccess: () => {
      toast.success(t("customerSupportChats.reopenSuccess"));
      setReopenTarget(null);
      void queryClient.invalidateQueries({
        queryKey: customerSupportChatKeys.all,
      });
      setSelectedId(null);
    },
    onError: (err) => toastApiError(err),
  });

  const amendMutation = useMutation({
    mutationFn: ({
      bookingId,
      visit_at,
      time_slot,
      override_capacity,
    }: {
      bookingId: string;
      visit_at: string;
      time_slot: string;
      override_capacity: boolean;
    }) =>
      bookingService.amendBooking(bookingId, {
        visit_at,
        time_slot,
        override_capacity,
      }),
    onSuccess: () => {
      toast.success(t("customerSupportChats.amendSuccess"));
      setAmendBookingId(null);
      setAmendActivityRegionCode(null);
      if (selectedId) {
        void queryClient.invalidateQueries({
          queryKey: customerSupportChatKeys.context(selectedId),
        });
      }
    },
    onError: (err) => toastApiError(err),
  });

  const cancelMutation = useMutation({
    mutationFn: ({
      bookingId,
      refund_choice,
    }: {
      bookingId: string;
      refund_choice: RefundChoice;
    }) => bookingService.cancelBookingAdmin(bookingId, { refund_choice }),
    onSuccess: () => {
      toast.success(t("customerSupportChats.cancelSuccess"));
      setCancelBookingId(null);
      setForceCancelConfirmOpen(false);
      if (selectedId) {
        void queryClient.invalidateQueries({
          queryKey: customerSupportChatKeys.context(selectedId),
        });
      }
    },
    onError: (err) => {
      setForceCancelConfirmOpen(false);
      toastApiError(err);
    },
  });

  const chats = listQuery.data?.items ?? [];
  const selectedChat = detailQuery.data;
  const emptyListTitle =
    statusFilter === "open"
      ? t("customerSupportChats.emptyOpen")
      : t("customerSupportChats.empty");
  const uiMessages = useMemo(
    () => chatMessagesToUi(selectedChat?.messages ?? []),
    [selectedChat?.messages],
  );
  const lastHandshakeBookingId = useMemo(() => {
    const handshakes = contextQuery.data?.handshakes ?? [];
    if (handshakes.length === 0) {
      return null;
    }

    const latestHandshake = [...handshakes].sort((left, right) => {
      const leftLatestAt =
        left.messages.at(-1)?.created_at ?? "";
      const rightLatestAt =
        right.messages.at(-1)?.created_at ?? "";
      return rightLatestAt.localeCompare(leftLatestAt);
    })[0];

    return latestHandshake?.booking_id ?? null;
  }, [contextQuery.data?.handshakes]);

  const submitCancel = () => {
    if (!cancelBookingId) return;
    cancelMutation.mutate({
      bookingId: cancelBookingId,
      refund_choice: refundChoice,
    });
  };

  return (
    <>
    <MasterDetailShell
      listWidth="narrow"
      list={
        <ListResultsCard
          className="flex min-h-0 flex-1 flex-col overflow-hidden"
          hideHeader
          contentClassName="flex min-h-0 flex-1 flex-col p-0"
          beforeContent={
            <>
              <div className="shrink-0 space-y-2 border-b border-surface-200/70 p-3">
                <SearchField
                  searchLabel={tCommon("search")}
                  clearSearchLabel={tCommon("clearSearch")}
                  value={search}
                  onChange={(value) => {
                    setSearch(value);
                    setPage(1);
                  }}
                  placeholder={t("customerSupportChats.searchPlaceholder")}
                  className="max-w-none"
                />
                <div className="flex items-center justify-between gap-2">
                  <FilterBar>
                    <AdminFilterMenu<StatusFilter>
                      icon={CircleDot}
                      name={t("customerSupportChats.statusFilter")}
                      value={statusFilter}
                      defaultValue="open"
                      onChange={(value) => {
                        setStatusFilter(value);
                        setPage(1);
                      }}
                      options={[
                        {
                          value: "open",
                          label: t("customerSupportChats.filterOpen"),
                        },
                        {
                          value: "closed",
                          label: t("customerSupportChats.filterClosed"),
                        },
                        {
                          value: "all",
                          label: t("customerSupportChats.filterAll"),
                        },
                      ]}
                    />
                  </FilterBar>
                  <Button
                    variant="ghost"
                    size="sm"
                    isLoading={listQuery.isFetching}
                    leftIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={() => void listQuery.refetch()}
                    aria-label={tCommon("refresh")}
                  />
                </div>
              </div>
            </>
          }
          isLoading={listQuery.isLoading}
          loadingMessage={t("customerSupportChats.loading")}
          isError={listQuery.isError}
          errorState={<ErrorState onRetry={() => void listQuery.refetch()} />}
          isEmpty={chats.length === 0}
          emptyState={
            <EmptyState
              size="md"
              icon={MessageCircle}
              title={emptyListTitle}
            />
          }
          pagination={
            <Pagination
              labels={tableLabels}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              isLoading={listQuery.isFetching}
              placement="bottom"
              className="shrink-0 border-surface-200/70 bg-surface-0 px-3 py-2"
            />
          }
        >
          <div className="min-h-0 flex-1 overflow-y-auto">
            {chats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                selected={chat.id === selectedId}
                onSelect={() => selectChat(chat.id)}
              />
            ))}
          </div>
        </ListResultsCard>
      }
      detail={
        <div className="flex min-h-0 h-full flex-col gap-4 lg:flex-row lg:items-stretch">
          <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <CardContent className="flex min-h-0 flex-1 flex-col p-0">
              {!selectedId ? (
                <EmptyState
                  icon={MessageCircle}
                  title={t("customerSupportChats.selectChat")}
                />
              ) : detailQuery.isLoading ? (
                <LoadingState message={t("customerSupportChats.loadingChat")} />
              ) : selectedChat ? (
                <MessengerChat
                  className="min-h-0 flex-1"
                  minHeightClassName="h-full min-h-0"
                  title={selectedChat.customer_contact}
                  subtitle={
                    selectedChat.is_guest
                      ? t("customerSupportChats.guest")
                      : t("customerSupportChats.registeredCustomer")
                  }
                  messages={uiMessages}
                  emptyText={t("customerSupportChats.noMessages")}
                  onRefresh={() => {
                    setIsRefreshingChat(true);
                    void detailQuery
                      .refetch()
                      .finally(() => setIsRefreshingChat(false));
                  }}
                  isRefreshing={isRefreshingChat}
                  footer={
                    selectedChat.status === "CLOSED"
                      ? {
                          kind: "closed",
                          node: t("customerSupportChats.chatClosed"),
                        }
                      : {
                          kind: "input",
                          onSend: async (text) => {
                            await sendMutation.mutateAsync({
                              chatId: selectedChat.id,
                              content: text,
                            });
                          },
                          isSending: sendMutation.isPending,
                          placeholder: t(
                            "customerSupportChats.replyPlaceholder",
                          ),
                          disabled: selectedChat.status !== "OPEN",
                        }
                  }
                  headerExtra={
                    selectedChat.status === "OPEN" ? (
                      <Button
                        variant="outline"
                        size="sm"
                        isLoading={closeMutation.isPending}
                        onClick={() => closeMutation.mutate(selectedChat.id)}
                      >
                        {t("customerSupportChats.closeChat")}
                      </Button>
                    ) : null
                  }
                />
              ) : null}
            </CardContent>
          </Card>

          {selectedId ? (
            <div className="flex min-h-0 w-full shrink-0 flex-col gap-4 overflow-y-auto lg:w-80">
              <ListResultsCard
                title={t("customerSupportChats.recentBookings")}
                contentClassName="p-4"
                className="shrink-0"
                isLoading={contextQuery.isLoading}
                loadingMessage={t("customerSupportChats.loading")}
                isError={contextQuery.isError}
                errorState={
                  <ErrorState onRetry={() => void contextQuery.refetch()} />
                }
                isEmpty={
                  !contextQuery.isLoading &&
                  !contextQuery.isError &&
                  (contextQuery.data?.bookings.length ?? 0) === 0
                }
                emptyState={
                  <EmptyState
                    size="sm"
                    showIcon={false}
                    title={t("customerSupportChats.noBookings")}
                  />
                }
              >
                <ul className="space-y-3">
                  {contextQuery.data?.bookings.map((booking) => (
                    <li
                      key={booking.id}
                      className="rounded-lg border border-surface-200 bg-surface-50 px-3.5 py-3 text-xs dark:bg-surface-100/40"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 space-y-1">
                          <p className="truncate text-sm font-semibold text-surface-900">
                            {booking.activity_name ?? booking.id.slice(0, 8)}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <BookingStatusBadge
                              status={booking.status}
                              handshakeStatus={null}
                              size="sm"
                            />
                            {booking.id === lastHandshakeBookingId ? (
                              <Badge variant="primary" size="sm">
                                {t("customerSupportChats.lastHandshakeBadge")}
                              </Badge>
                            ) : null}
                          </div>
                        </div>
                        <Button
                          size="xs"
                          variant="secondary"
                          leftIcon={<Eye className="h-3.5 w-3.5" />}
                          onClick={() => navigate(`/bookings/${booking.id}`)}
                        >
                          {t("customerSupportChats.openBooking")}
                        </Button>
                      </div>
                      {booking.visit_at ? (
                        <p className="mt-2 text-surface-600">
                          {formatBookingVisitWhen(
                            booking.visit_at,
                            booking.time_slot,
                            booking.activity_region_code,
                          )}
                        </p>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        {canAmendBookings && booking.can_amend ? (
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<CalendarClock className="h-3.5 w-3.5" />}
                            onClick={() => {
                              setAmendBookingId(booking.id);
                              setAmendActivityRegionCode(booking.activity_region_code);
                              if (booking.visit_at) {
                                setVisitDate(
                                  visitAtIsoToDateInput(
                                    booking.visit_at,
                                    booking.activity_region_code,
                                  ) ?? "",
                                );
                                setTimeSlot(
                                  booking.time_slot
                                    ? scheduleTimeToHHmm(booking.time_slot)
                                    : visitAtIsoToTimeInput(
                                        booking.visit_at,
                                        booking.activity_region_code,
                                      ) ?? "10:00",
                                );
                              } else {
                                setVisitDate("");
                                setTimeSlot(
                                  booking.time_slot
                                    ? scheduleTimeToHHmm(booking.time_slot)
                                    : "10:00",
                                );
                              }
                              setOverrideCapacity(false);
                            }}
                          >
                            {t("customerSupportChats.amend")}
                          </Button>
                        ) : null}
                        {canReopenHandshake &&
                        booking.can_reopen_handshake &&
                        booking.handshake_session_id &&
                        selectedChat?.status === "OPEN" &&
                        selectedId ? (
                          <Button
                            size="xs"
                            variant="outline"
                            leftIcon={<Handshake className="h-3.5 w-3.5" />}
                            isLoading={reopenMutation.isPending}
                            onClick={() => {
                              const sessionId = booking.handshake_session_id;
                              if (!sessionId) return;
                              setReopenTarget({
                                chatId: selectedId,
                                sessionId,
                              });
                            }}
                          >
                            {t("customerSupportChats.reopenHandshake")}
                          </Button>
                        ) : null}
                        {canCancelAnyBooking && booking.status === "ACCEPTED" ? (
                          <Button
                            size="xs"
                            variant="ghost"
                            className="text-error-600 hover:bg-error-50 hover:text-error-700"
                            leftIcon={<XCircle className="h-3.5 w-3.5" />}
                            onClick={() => {
                              setCancelBookingId(booking.id);
                              setRefundChoice("DEFAULT");
                            }}
                          >
                            {t("customerSupportChats.cancelBooking")}
                          </Button>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>
              </ListResultsCard>
            </div>
          ) : null}
        </div>
      }
    />

      <Modal
        closeLabel={tCommon("close")}
        isOpen={amendBookingId != null}
        onClose={() => {
          setAmendBookingId(null);
          setAmendActivityRegionCode(null);
        }}
        title={t("customerSupportChats.amendTitle")}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAmendBookingId(null);
                setAmendActivityRegionCode(null);
              }}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              disabled={
                amendMutation.isPending || !visitDate || !timeSlot
              }
              isLoading={amendMutation.isPending}
              onClick={() => {
                if (!amendBookingId) return;
                amendMutation.mutate({
                  bookingId: amendBookingId,
                  visit_at: visitAtLocalPickerToApiIso(visitDate, timeSlot),
                  time_slot: scheduleTimeToHHmm(timeSlot),
                  override_capacity: overrideCapacity,
                });
              }}
            >
              {t("customerSupportChats.amendConfirm")}
            </Button>
          </ModalFooter>
        }
      >
        <div className="space-y-4">
          <Input
            type="date"
            label={t("customerSupportChats.visitDateLabel")}
            value={visitDate}
            min={todayActivityLocalDateInput(amendActivityRegionCode)}
            onChange={(e) => setVisitDate(e.target.value)}
          />
          <Input
            type="time"
            label={t("customerSupportChats.visitTimeLabel")}
            value={timeSlot}
            onChange={(e) => setTimeSlot(scheduleTimeToHHmm(e.target.value))}
            placeholder="HH:MM"
          />
          <Switch
            checked={overrideCapacity}
            onChange={(e) => setOverrideCapacity(e.target.checked)}
            label={t("customerSupportChats.overrideCapacity")}
          />
        </div>
      </Modal>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={cancelBookingId != null}
        onClose={() => {
          setCancelBookingId(null);
          setForceCancelConfirmOpen(false);
        }}
        title={t("customerSupportChats.cancelTitle")}
        size="sm"
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setCancelBookingId(null);
                setForceCancelConfirmOpen(false);
              }}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              variant="error"
              disabled={cancelMutation.isPending}
              isLoading={cancelMutation.isPending}
              onClick={() => {
                if (
                  refundChoice === "FORCE_REFUND" ||
                  refundChoice === "FORCE_NO_REFUND"
                ) {
                  setForceCancelConfirmOpen(true);
                  return;
                }
                submitCancel();
              }}
            >
              {t("customerSupportChats.cancelConfirm")}
            </Button>
          </ModalFooter>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-surface-600">
            {t("customerSupportChats.cancelDescriptionWithDetails", {
              name: selectedChat?.customer_contact ?? "—",
              activity:
                contextQuery.data?.bookings.find((b) => b.id === cancelBookingId)
                  ?.activity_name ?? "—",
            })}
          </p>
          <Select
            label={t("customerSupportChats.refundChoiceLabel")}
            value={refundChoice}
            onChange={(e) =>
              setRefundChoice(e.target.value as RefundChoice)
            }
            options={
              canForceRefund
                ? [
                    { value: "DEFAULT", label: t("customerSupportChats.refundDefault") },
                    { value: "FORCE_REFUND", label: t("customerSupportChats.refundForce") },
                    { value: "FORCE_NO_REFUND", label: t("customerSupportChats.refundNone") },
                  ]
                : [
                    { value: "DEFAULT", label: t("customerSupportChats.refundDefault") },
                  ]
            }
          />
        </div>
      </Modal>

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={reopenTarget != null}
        onClose={() => setReopenTarget(null)}
        onConfirm={() => {
          if (!reopenTarget) return;
          reopenMutation.mutate(reopenTarget);
        }}
        title={t("customerSupportChats.reopenConfirmTitle")}
        message={t("customerSupportChats.reopenConfirmMessage")}
        confirmText={t("customerSupportChats.reopenConfirm")}
        isLoading={reopenMutation.isPending}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
       
        isOpen={forceCancelConfirmOpen}
        onClose={() => setForceCancelConfirmOpen(false)}
        onConfirm={submitCancel}
        title={t("customerSupportChats.cancelForceConfirmTitle")}
        message={
          refundChoice === "FORCE_NO_REFUND"
            ? t("customerSupportChats.cancelForceNoneMessage")
            : t("customerSupportChats.cancelForceRefundMessage")
        }
        confirmText={t("customerSupportChats.cancelConfirm")}
        isDestructive
        isLoading={cancelMutation.isPending}
      />
    </>
  );
}
